import React, { useState } from "react";
import { ValidationResultReport, BankValidation, SourceResolveDecision } from "../../../api/apiTypes";
import { PendingSourceDecision } from "../../../api/beSourceDecisions";
import { EsitoAzione, EsitoResolve } from "../DialogNewReportForm";
import { formatSnakeToTitle } from "../../Format";
import { ValidationBadge } from "../../ValidationBadge";
import {
  canCreateReport,
  classesWithLabel,
  hasLabel,
  shouldKeepPolling,
} from "../../validationLabels";

interface Props {
  validationResult: ValidationResultReport;
  expandedBanks: Record<string, boolean>;
  toggleBankAccordion: (bankName: string) => void;
  handleIndicizzaFonti: (force?: boolean) => void;
  handleCheckSources: () => void;
  handleResolve: (
    decision: SourceResolveDecision,
    pending: PendingSourceDecision,
    newUri?: string,
  ) => void;
  handleNextStep: () => void;
  handlePrevStep: () => void;
  loadingIndexing: boolean;
  loadingCheck: boolean;
  loadingDecisions: boolean;
  resolvingSourceId: string | null;
  pendingDecisions: PendingSourceDecision[];
  /** Vero nella finestra fra l'avvio delle pipeline e la conferma da /validate. */
  avvioRecente: boolean;
  /** Esito dell'ultima decisione presa su una fonte cambiata. */
  esitoResolve: EsitoResolve | null;
  /** Esito della sola ultima azione (indicizzazione o controllo fonti). */
  esitoAzione: EsitoAzione | null;
  /** Errore di una chiamata, mostrato dentro il wizard invece che in un popup. */
  erroreWizard: string;
}

/** `pending` = azione in volo: neutro, non afferma ancora nessun esito. */
type StatusType = "error" | "action" | "info" | "pending";

interface Step2Status {
  message: string;
  statusType: StatusType;
  /** Azione principale suggerita, oltre a quelle sempre disponibili. */
  primary?: "ingest" | "resolve" | "create";
}

/** Elenca le classi che portano una label, come "Banca X / pillar_iii". */
const listClasses = (r: ValidationResultReport, label: string, max = 4): string => {
  const items = classesWithLabel(r, label).map(
    ({ entity, documentType }) => `${formatSnakeToTitle(entity)}/${formatSnakeToTitle(documentType)}`,
  );
  if (items.length <= max) return items.join(", ");
  return `${items.slice(0, max).join(", ")} e altre ${items.length - max}`;
};

const Step2Validation: React.FC<Props> = ({
  validationResult,
  expandedBanks,
  toggleBankAccordion,
  handleIndicizzaFonti,
  handleCheckSources,
  handleResolve,
  handleNextStep,
  handlePrevStep,
  loadingIndexing,
  loadingCheck,
  loadingDecisions,
  resolvingSourceId,
  pendingDecisions,
  avvioRecente,
  esitoResolve,
  esitoAzione,
  erroreWizard,
}) => {
  /**
   * Il via libera arriva solo da `status: "OK"` del BE.
   *
   * La versione precedente derivava lo stato da una catena di if sulle label note,
   * e una label sconosciuta cadeva nel ramo finale "tutto valido": una banca con
   * solo `source_changed` passava il controllo e poi la creazione falliva con 409.
   */
  const canCreate = canCreateReport(validationResult);
  const polling = shouldKeepPolling(validationResult);

  const getStep2Status = (): Step2Status => {
    // Mentre la POST è in volo non si sa ancora se qualcosa è partito davvero:
    // il messaggio resta neutro. La conferma arriva da /validate, che segnala
    // `indexing_in_progress` — è quello il ramo che può dire "ci vorrà del tempo".
    if (loadingIndexing) {
      return {
        message: "Invio della richiesta di indicizzazione…",
        statusType: "pending",
      };
    }

    if (loadingCheck) {
      return {
        message: "Invio della richiesta di controllo fonti…",
        statusType: "pending",
      };
    }

    if (canCreate) {
      return {
        message: "Tutte le banche sono valide. Puoi procedere con la creazione del report.",
        statusType: "info",
        primary: "create",
      };
    }

    // In ordine: prima quello che richiede un'azione dell'utente, poi le attese.
    if (hasLabel(validationResult, "missing_source")) {
      return {
        message: `Ci sono classi senza fonte configurata (${listClasses(
          validationResult,
          "missing_source",
        )}). Registrale dalla scheda della banca, poi torna qui.`,
        statusType: "error",
      };
    }

    if (hasLabel(validationResult, "bank_not_found")) {
      return {
        message:
          "Una o più banche non sono state trovate lato server: è un problema di configurazione, contatta il supporto.",
        statusType: "error",
      };
    }

    if (hasLabel(validationResult, "source_changed")) {
      return {
        message: `Alcune fonti sono cambiate all'origine (${listClasses(
          validationResult,
          "source_changed",
        )}). Decidi se tenere la versione registrata o passare a quella nuova.`,
        statusType: "action",
        primary: "resolve",
      };
    }

    // Prima di dire "devi indicizzare", controlla che non stia già girando:
    // `avvioRecente` copre i secondi fra la risposta di /ingest e il momento in
    // cui /validate comincia a riportare `indexing_in_progress`.
    if (avvioRecente || hasLabel(validationResult, "indexing_in_progress")) {
      return {
        message:
          "Indicizzazione in corso. Ci vorrà del tempo: la pagina si aggiorna da sola, ma puoi chiudere e tornare più tardi a controllare.",
        statusType: "info",
      };
    }

    if (hasLabel(validationResult, "indexation_needed")) {
      return {
        message:
          "Esistono delle banche con fonti da indicizzare. È necessario procedere all'indicizzazione prima della creazione del report.",
        statusType: "error",
        primary: "ingest",
      };
    }

    if (hasLabel(validationResult, "old_data")) {
      return {
        message: `Ci sono fonti obsolete (${listClasses(
          validationResult,
          "old_data",
        )}): registra un documento più recente.`,
        statusType: "action",
      };
    }

    if (hasLabel(validationResult, "check_in_progress")) {
      return {
        message: "Controllo freschezza delle fonti in corso. La pagina si aggiorna da sola.",
        statusType: "info",
      };
    }

    if (hasLabel(validationResult, "error")) {
      return {
        message: "Il processo di indicizzazione è andato in errore. Contatta il supporto.",
        statusType: "error",
      };
    }

    // Il BE dice NOT OK ma non riconosciamo il motivo: non inventiamo un via libera.
    return {
      message:
        "Il server segnala che i prerequisiti non sono soddisfatti. Controlla il dettaglio per banca qui sotto.",
      statusType: "error",
    };
  };

  const status = getStep2Status();

  /**
   * Form di switch inline: quale fonte si sta sostituendo e l'URL nuovo, se il
   * documento si e' spostato. Non un popup — il wizard e' a sua volta un popup
   * SweetAlert e una `Swal.fire` annidata lo sostituirebbe.
   */
  const [switchAperto, setSwitchAperto] = useState<string | null>(null);
  const [nuovoUri, setNuovoUri] = useState("");
  const [erroreUri, setErroreUri] = useState("");

  const apriSwitch = (sourceId: string) => {
    setSwitchAperto(sourceId);
    setNuovoUri("");
    setErroreUri("");
  };

  const confermaSwitch = (pending: PendingSourceDecision) => {
    const uri = nuovoUri.trim();
    if (uri) {
      try {
        new URL(uri);
      } catch {
        setErroreUri("URL non valido");
        return;
      }
    }
    setSwitchAperto(null);
    handleResolve("switch", pending, uri || undefined);
  };

  /** Banche + general: anche le classi generali bloccano la creazione. */
  const entities: Array<[string, BankValidation]> = [
    ...Object.entries(validationResult.banks ?? {}),
    ...(validationResult.general
      ? ([["general", validationResult.general]] as Array<[string, BankValidation]>)
      : []),
  ];

  return (
    <div className="flex flex-col gap-4 max-h-[70vh]">
      <div
        className="overflow-y-auto max-h-[60vh] pr-2 flex flex-col gap-4"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {/* Status message */}
        <div
          className={`p-2 rounded border
            ${status.statusType === "error" ? " text-red-800 border-red-300" : ""}
            ${status.statusType === "action" ? " text-yellow-800 border-yellow-300" : ""}
            ${status.statusType === "info" ? " text-green-800 border-green-300" : ""}
            ${status.statusType === "pending" ? " text-gray-700 border-gray-300" : ""}
          `}
        >
          {status.message}
          {polling && (
            <span className="ml-2 text-xs text-textSecondary">
              (aggiornamento automatico ogni 5 secondi)
            </span>
          )}
        </div>

        {erroreWizard && (
          <div className="border border-red-300 rounded p-3 text-sm text-red-800 text-left">
            {erroreWizard}
          </div>
        )}

        {esitoAzione?.avviso && (
          <div className="border border-yellow-300 bg-yellow-50 rounded p-3 text-sm text-yellow-900 text-left">
            {esitoAzione.avviso}
          </div>
        )}

        {esitoAzione && (
          <div className="text-xs text-textSecondary text-left">
            {esitoAzione.titolo}: {esitoAzione.testo}
          </div>
        )}

        {/* Esito dell'ultima decisione. Sta fuori dal banner: appena la fonte è
            risolta `source_changed` sparisce da /validate, e con lui sparirebbe
            anche la conferma di quello che è appena successo. */}
        {esitoResolve && (
          <div
            className={`rounded p-3 text-sm text-left border ${
              esitoResolve.tono === "errore"
                ? "border-red-300 text-red-800"
                : esitoResolve.tono === "info"
                  ? "border-gray-300 text-gray-700"
                  : "border-green-300 text-green-800"
            }`}
          >
            <div>{esitoResolve.messaggio}</div>
            {esitoResolve.daIndicizzare.length > 0 && (
              <>
                <div className="mt-1 text-xs text-textSecondary">
                  Da reindicizzare: {esitoResolve.daIndicizzare.join(", ")}
                </div>
                <button
                  onClick={() => handleIndicizzaFonti(false)}
                  disabled={loadingIndexing || loadingCheck}
                  className="mt-2 bg-textPrimary text-white hover:bg-textSecondary px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingIndexing ? "Indicizzazione…" : "Indicizza ora"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Banner decisione su fonti cambiate */}
        {hasLabel(validationResult, "source_changed") && (
          <div className="border border-yellow-300 rounded p-3 flex flex-col gap-3">
            <div className="font-semibold text-yellow-800">Fonti cambiate all'origine</div>

            {loadingDecisions && (
              <div className="text-sm text-textSecondary">Individuazione delle fonti in corso…</div>
            )}

            {!loadingDecisions && pendingDecisions.length === 0 && (
              <div className="text-sm text-textSecondary">
                Il server segnala una fonte cambiata ma non è stato possibile individuarla. Riprova
                il controllo fonti, oppure contatta il supporto.
              </div>
            )}

            {pendingDecisions.map((pending) => {
              const busy = resolvingSourceId === pending.source.id;
              return (
                <div
                  key={`${pending.documentClassId}-${pending.source.id}`}
                  className="flex flex-col gap-2 border-t border-yellow-200 pt-2 first:border-t-0 first:pt-0"
                >
                  <div className="text-sm text-left">
                    <span className="font-medium">
                      {formatSnakeToTitle(pending.entity)} / {formatSnakeToTitle(pending.documentType)}
                    </span>
                    <div className="text-xs text-textSecondary break-all">
                      {pending.source.filename || pending.source.uri}
                    </div>
                    <div className="text-xs text-textSecondary">
                      Cambiamento rilevato il{" "}
                      {new Date(pending.lastCheck.checked_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                  {switchAperto === pending.source.id ? (
                    <div className="flex flex-col gap-2 bg-white border border-yellow-200 rounded p-2">
                      <p className="text-xs text-textSecondary text-left">
                        I bytes attualmente serviti dall'origine diventano una nuova fonte che
                        sostituisce quella registrata. Tutti i collegamenti attivi si spostano,
                        anche su altre banche che condividono questa fonte. Dopo lo switch serve
                        reindicizzare.
                      </p>
                      <input
                        type="text"
                        value={nuovoUri}
                        onChange={(e) => {
                          setNuovoUri(e.target.value);
                          if (erroreUri) setErroreUri("");
                        }}
                        placeholder="Il documento si è spostato a questo URL (opzionale)"
                        className="w-full p-2 border border-divider rounded text-sm"
                      />
                      {erroreUri && <span className="text-xs text-red-600">{erroreUri}</span>}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSwitchAperto(null)}
                          disabled={busy}
                          className="bg-gray-300 text-gray-800 hover:bg-gray-400 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={() => confermaSwitch(pending)}
                          disabled={busy}
                          className="bg-textPrimary text-white hover:bg-textSecondary px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {busy ? "…" : "Conferma il passaggio"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleResolve("keep", pending)}
                        disabled={busy}
                        className="bg-gray-300 text-gray-800 hover:bg-gray-400 px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {busy ? "…" : "Tieni la versione attuale"}
                      </button>
                      <button
                        onClick={() => apriSwitch(pending.source.id)}
                        disabled={busy}
                        className="bg-textPrimary text-white hover:bg-textSecondary px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {busy ? "…" : "Passa alla nuova versione"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Accordion per banche (+ classi generali) */}
        {entities.map(([entityName, entityData]) => {
          const isExpanded = expandedBanks[entityName] ?? false;

          return (
            <div key={entityName} className="border rounded">
              <div
                className="flex justify-between items-center p-2 cursor-pointer text-buttonPrimaryText"
                onClick={() => toggleBankAccordion(entityName)}
              >
                <div className="flex w-full items-center">
                  <div className="w-5/12 flex justify-start ml-2">
                    <span className="font-semibold text-textPrimary">
                      {entityName === "general" ? "Fonti generali" : formatSnakeToTitle(entityName)}
                    </span>
                  </div>
                  <div className="w-5/12 flex justify-start">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        entityData.status === "OK"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {entityData.status === "OK" ? "Verificato" : "Da verificare"}
                    </span>
                  </div>
                  <div className="w-2/12 flex justify-center">
                    <span>{isExpanded ? "▲" : "▼"}</span>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="p-2 flex flex-col gap-2">
                  {Object.entries(entityData.document_classes ?? {}).map(([docClass, docData]) => {
                    // Ogni label va mostrata con la sua lista di provenienza: prima si
                    // mostrava solo la lista piu' "grave" non vuota, e le info bloccanti
                    // (check_in_progress) sparivano dietro un'action.
                    const badges: Array<{ type: "error" | "action" | "info"; value: string }> = [
                      ...(docData.errors ?? []).map((v) => ({ type: "error" as const, value: v })),
                      ...(docData.actions ?? []).map((v) => ({ type: "action" as const, value: v })),
                      ...(docData.info ?? []).map((v) => ({ type: "info" as const, value: v })),
                    ];

                    return (
                      <div
                        key={docClass}
                        className="flex items-center py-2 border-b border-gray-200 w-full text-black"
                      >
                        <div className="w-5/12 flex justify-start ml-2">
                          <span className="text-textSecondary font whitespace-pre-line text-left">
                            {formatSnakeToTitle(docClass)}
                          </span>
                        </div>
                        <div className="flex-1 flex gap-2 flex-wrap">
                          {badges.length === 0 ? (
                            <ValidationBadge type="info" value="Completato" />
                          ) : (
                            badges.map((b, i) => (
                              <ValidationBadge key={`${b.value}-${i}`} type={b.type} value={b.value} />
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Azioni: Indietro a sinistra, il resto a destra.
            Le tre azioni sulle fonti e "Crea report" si escludono a vicenda —
            finché il server non dà l'ok ci sono le azioni, quando l'ha dato
            resta solo la creazione. */}
        <div className="flex mt-6 pt-4 justify-between items-center gap-x-2 gap-y-3 flex-wrap">
          <button
            onClick={handlePrevStep}
            className="bg-gray-300 text-gray-800 hover:bg-gray-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center"
          >
            Indietro
          </button>

          <div className="flex gap-2 flex-wrap justify-end ml-auto">
            {canCreate ? (
              <>
                {/* Il documento colloca il controllo freschezza proprio qui, fra
                    l'ingestione pulita e la creazione: puo' far emergere una fonte
                    cambiata e riportare la validazione a NOT OK. */}
                <button
                  onClick={handleCheckSources}
                  disabled={loadingIndexing || loadingCheck}
                  title="Ri-scarica le fonti remote e le confronta con quelle registrate. Ogni controllo è un download vero."
                  className="bg-gray-300 text-gray-800 hover:bg-gray-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingCheck ? "Controllo…" : "Controlla fonti"}
                </button>
                <button
                  onClick={handleNextStep}
                  className="bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded-lg text-sm font-medium flex items-center"
                >
                  Crea report
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleIndicizzaFonti(false)}
                  disabled={loadingIndexing || loadingCheck}
                  title="Avvia l'indicizzazione delle unità non ancora indicizzate. Lavora sui bytes già salvati, non scarica nulla."
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed ${
                    status.primary === "ingest"
                      ? "bg-textPrimary text-white hover:bg-textSecondary"
                      : "bg-gray-300 text-gray-800 hover:bg-gray-400"
                  }`}
                >
                  {loadingIndexing ? "Indicizzazione…" : "Indicizza fonti"}
                </button>

                <button
                  onClick={() => handleIndicizzaFonti(true)}
                  disabled={loadingIndexing || loadingCheck}
                  title="Stesso endpoint di «Indicizza fonti» ma rilancia anche le unità già indicizzate. Serve anche a recuperare una run rimasta appesa."
                  className="bg-gray-300 text-gray-800 hover:bg-gray-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Riprocessa tutto
                </button>

                <button
                  onClick={handleCheckSources}
                  disabled={loadingIndexing || loadingCheck}
                  title="Ri-scarica le fonti remote e le confronta con quelle registrate. Ogni controllo è un download vero."
                  className="bg-gray-300 text-gray-800 hover:bg-gray-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingCheck ? "Controllo…" : "Controlla fonti"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2Validation;
